import { useLayoutEffect } from "react"
import {
  createBrowserRouter,
  type Location,
  type NonIndexRouteObject,
  Outlet,
  redirect,
  type RouteObject,
  RouterProvider,
  ScrollRestoration,
} from "react-router-dom"

import { getSession } from "../../_demo/session"
import { ExistingDocument, NewDocument } from "../../document"
import { DocumentList } from "../../documents/DocumentList"
import { MigrationReleasePage } from "../../documents/migrationRelease/MigrationReleasePage"
import {
  prefetchReleasePage,
  ReleasePage,
} from "../../documents/releases/ReleasePage"
import { ReleasesList } from "../../documents/releases/ReleasesList"
import { Lab } from "../../lab"
import { MediaLibraryPage } from "../../medias/MediaLibraryPage"
import { OnboardingPage } from "../../onboarding/OnboardingPage"
import {
  showDocListIfAlreadyOnboarded,
  showOnboardingIfNotOnboardedYet,
} from "../../onboarding/onboardingRedirection"
import { LocalesPage } from "../../settings/locales/LocalesPage"
import { GlobalBanners } from "../banners/GlobalBanners"
import { RouterError } from "../components/AppErrorHandler"
import { E2eReminder } from "../components/E2EReminder"
import { SuperUserReminder } from "../components/SuperUserReminder"
import { prefetchCustomTypes } from "../services/customTypes"
import { preloadRepository } from "../services/repository"
import { preloadProfile } from "../services/user"
import { isLinkState } from "./Link"
import { getRouterBasename } from "./repository"
import { RouteStateProvider } from "./RouteStateContext"

type RouteId =
  | "index"
  | "existingDocument"
  | "newDocument"
  | "medias"
  | "working"
  | "archived"
  | "upcoming"
  | "upcoming-detail"
  | "migration"
  | "locales"
  | "onboarding"
  | "lab"

export type PageBuilderRoute = NonIndexRouteObject & { id: RouteId }

const routes: RouteObject[] = [
  {
    path: "/",
    element: <RootRoute />,
    errorElement: <RouterError />,
    loader: prefetchApp,
    children: [
      {
        loader: showOnboardingIfNotOnboardedYet,
        children: [
          {
            id: "index",
            path: "/",
            loader: async () => {
              const { defaultDocumentId } = await getSession()

              // Preserve the existing search params
              const currentSearch = new URLSearchParams(window.location.search)
              currentSearch.set("s", "unclassified")

              return redirect(
                `/pages/${defaultDocumentId}/?${currentSearch.toString()}`,
              )
            },
          },
          {
            id: "working",
            path: "working",
            element: <DocumentList tab="working" />,
          },
          {
            id: "upcoming",
            path: "upcoming",
            element: <ReleasesList />,
          },
          {
            id: "upcoming-detail",
            path: "upcoming/:releaseId",
            element: <ReleasePage />,
            loader: (args) => prefetchReleasePage(args.params),
          },
          {
            id: "archived",
            path: "archived",
            element: <DocumentList tab="archived" />,
          },
          {
            id: "migration",
            path: "migration",
            element: <MigrationReleasePage />,
          },
          {
            id: "existingDocument",
            path: "pages/:documentId",
            element: <ExistingDocument />,
          },
          {
            id: "newDocument",
            path: "pages/new",
            element: <NewDocument />,
          },
        ],
      },
      {
        id: "onboarding",
        path: "onboarding",
        element: <OnboardingPage />,
        loader: showDocListIfAlreadyOnboarded,
      },
      {
        id: "medias",
        path: "medias",
        element: <MediaLibraryPage />,
      },
      {
        id: "locales",
        path: "settings/locales",
        element: <LocalesPage />,
      },
      {
        id: "lab",
        path: "lab",
        element: <Lab />,
      },
    ],
  },
]

export const router = createBrowserRouter(routes, {
  basename: getRouterBasename(),
})

export function AppRouterProvider() {
  return (
    <RouterProvider router={router} future={{ v7_startTransition: true }} />
  )
}

// Pure shared logic parent of all other routes.
// This shouldn't add any default/shared view/chrome, App.tsx can do that.
function RootRoute() {
  useLayoutEffect(() => {
    // We want to keep the HTML logo for as long as possible so it acts as a loading indicator and we never show a blank page.
    // Once the initial route is mounted, it means we can safely delegate to its dedicated skeleton.
    document.querySelector(".prismic-logo")?.remove()
  }, [])

  return (
    <>
      <SuperUserReminder />
      <E2eReminder />
      <ScrollRestoration getKey={getScrollRestorationKey} />
      <RouteStateProvider>
        <Outlet />
      </RouteStateProvider>
      <GlobalBanners />
    </>
  )
}

function prefetchApp() {
  preloadRepository()
  preloadProfile()
  prefetchCustomTypes()

  return null
}

// The default getKey is location.key (https://reactrouter.com/en/main/components/scroll-restoration#getkey)
// This doesn't work well with our Document list back button in particular as it's considered a new location (we're not merely calling history.back).
function getScrollRestorationKey(location: Location<unknown>) {
  if (isLinkState(location.state) && location.state.scrollToTop)
    return location.key

  return location.pathname
}
